import { computed, onMounted, onUnmounted, Ref, ref } from "@vue/composition-api";
import { MessagePrices } from "@scrile/api-provider/dist/api/MessagePricesProvider";
import { PaymentPackageEnum } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { ErrorDTO } from "@scrile/api-provider/dist/lib/apiErrorHandler";
import { Message } from "@scrile/api-provider/dist/projects/scrileConnect/MessagesProvider";
import { checkAuthorities } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAuthUser from "@/hooks/useAuthUser";
import usePaymentPackages from "@/hooks/usePaymentPackages";
import providers from "@/lib/api";
import createDeferred from "@/lib/deferredPromise";
import emitter from "@/lib/emitter";
import config from "@/config";
import { ERRORS, EVENTS } from "@/const";

const usePaidMessages = (userId?: string, forceFreeMessages?: Ref<boolean>) => {
  const { authUser, authorities } = useAuthUser();
  const { paymentPackages, getPaymentPackages } = usePaymentPackages([PaymentPackageEnum.PER_SENT_MESSAGE]);
  const messagePrices = ref<MessagePrices>();
  const subscriptionStatus = ref(false);

  const checkSubscriptionStatus = async (userId: string) => {
    if (!authUser.value) return;
    const subscriptions = await providers.SubscriptionProvider.search({
      data: {
        consumerIds: [authUser.value.id],
        providerId: userId,
        expired: false,
      },
    });
    if (subscriptions.result.length > 0) {
      subscriptionStatus.value = true;
    }
  };

  const getPriceData = async (userId: string) => {
    await Promise.all([
      checkSubscriptionStatus(userId),
      providers.MessagePricesProvider.find({ userIds: [userId] }).then(res => {
        messagePrices.value = res.find(i => i.userId === userId);
      }),
    ]);
  };
  const checkPromotion = () => {
    if (
      messagePrices.value?.perSentPriceId !== "0" &&
      messagePrices.value?.subscriberPerSentPriceId === "0" &&
      !subscriptionStatus.value &&
      userId
    ) {
      providers.SubscriptionLevelProvider.find({ userIds: [userId] }, ["contentCount"]).then(res => {
        if (res.some(l => l.contentCount ?? 0 > 0)) {
          emitter.emit(EVENTS.SUBSCRIPTION_OFFER_SHOW);
        }
      });
    }
  };
  const priceAmount = computed(() => {
    if (forceFreeMessages?.value) return 0;
    const priceId = subscriptionStatus.value
      ? messagePrices.value?.subscriberPerSentPriceId
      : messagePrices.value?.perSentPriceId;
    return paymentPackages.value.find(i => i.id === priceId)?.amount ?? 0;
  });
  const priceMediaAmount = computed(() => {
    if (forceFreeMessages?.value) return 0;
    const priceId = subscriptionStatus.value
      ? messagePrices.value?.subscriberPerSentWithMediaPriceId
      : messagePrices.value?.perSentWithMediaPriceId;
    return paymentPackages.value.find(i => i.id === priceId)?.amount ?? 0;
  });
  const priceAudioAmount = computed(() => {
    if (forceFreeMessages?.value) return 0;
    const priceId = subscriptionStatus.value
      ? messagePrices.value?.subscriberPerSentWithAudioPriceId
      : messagePrices.value?.perSentWithAudioPriceId;
    return paymentPackages.value.find(i => i.id === priceId)?.amount ?? 0;
  });

  const processingPayment = ref(false);
  const error = ref(false);
  const closeError = () => {
    error.value = false;
  };

  const isHasAuthoritiesToSendMessages = computed(() =>
    checkAuthorities(authorities.value, ["sendFreeMessageToPaidThreads"])
  );
  onMounted(() => {
    if (!userId || !config.featurePaidSentMessage || !authUser.value || isHasAuthoritiesToSendMessages.value) {
      return;
    }
    getPriceData(userId).then(() => checkPromotion());
    getPaymentPackages();
    emitter.on<any>(EVENTS.SUBSCRIPTION_STATUS_CHANGED, getPriceData);
  });
  onUnmounted(() => {
    emitter.off<any>(EVENTS.SUBSCRIPTION_STATUS_CHANGED, getPriceData);
  });

  const deferred = createDeferred();
  const showPaymentConfirm = ref(false);
  const showPaymentMediaConfirm = ref(false);
  const showPaymentAudioConfirm = ref(false);

  const onPaymentConfirm = (response: boolean) => {
    showPaymentConfirm.value = false;
    showPaymentMediaConfirm.value = false;
    showPaymentAudioConfirm.value = false;
    deferred.resolve(response);
    const newDeferred = createDeferred();
    deferred.promise = newDeferred.promise;
    deferred.resolve = newDeferred.resolve;
  };

  const createSendPaidMessage = (
    sendMessage: (text: string) => Promise<Message[] | undefined>,
    hasAttachments: boolean,
    isAudioMessage: boolean = false
  ) => {
    if (!config.featurePaidSentMessage || isHasAuthoritiesToSendMessages.value || !userId || forceFreeMessages?.value) {
      return sendMessage;
    }
    return async (text: string) => {
      if (!userId) {
        throw new Error("UserId required");
      }
      try {
        processingPayment.value = true;
        providers.MessagesProvider.setReceiveErrors(false);
        await getPriceData(userId);

        if (
          priceAmount.value > 0 ||
          (hasAttachments && priceMediaAmount.value > 0) ||
          (isAudioMessage && priceAudioAmount.value > 0)
        ) {
          if (isAudioMessage) {
            showPaymentAudioConfirm.value = true;
          } else if (hasAttachments && priceMediaAmount.value > 0) {
            showPaymentMediaConfirm.value = true;
          } else {
            showPaymentConfirm.value = true;
          }
          if (!(await deferred.promise)) {
            return;
          }
        }
        return await sendMessage(text);
      } catch (e) {
        if ((e as ErrorDTO[]).some(i => i?.details?.messageTemplate === ERRORS.SPENDING_BALANCE)) {
          error.value = true;
          return null;
        }
        throw e;
      } finally {
        processingPayment.value = false;
        showPaymentConfirm.value = false;
        showPaymentMediaConfirm.value = false;
      }
    };
  };

  return {
    createSendPaidMessage,
    priceAmount,
    priceMediaAmount,
    priceAudioAmount,
    processingPayment,
    showPaymentConfirm,
    showPaymentMediaConfirm,
    showPaymentAudioConfirm,
    onPaymentConfirm,
    error,
    closeError,
  };
};

export default usePaidMessages;
